import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import CtaPanel from '../components/CtaPanel';
import { masterBrands, additionalBrands } from '../data/brands';
import { company } from '../data/company';
import './About.css';

const VALUES = [
  { icon: 'award', title: 'Superior Quality', text: 'We supply only the highest quality materials from trusted manufacturers.' },
  { icon: 'layers', title: 'Smart Pricing', text: 'Competitive prices without compromising on quality. Get the best value for your construction and renovation projects.' },
  { icon: 'truck', title: 'Fast Delivery', text: 'Quick turnaround times and reliable delivery service across the Western and Eastern Cape to keep your projects on schedule.' },
  { icon: 'leaf', title: 'Green Building Expertise', text: 'As an accredited Green Building Council Professional, our owner can advise on the correct products for energy-efficient buildings.' },
  { icon: 'users', title: '30+ Years in the Industry', text: 'Led by Niel Kasselman, well-known in the building industry with more than 30 years of hands-on experience.' },
  { icon: 'pin', title: 'Convenient Location', text: 'Easily accessible location in Joubert Industria, George with ample parking and loading facilities.' },
];

const WHY_CHOOSE = [
  'SABISA Accredited',
  'Master distributor for SuperTec, ETEX, Siniat and OWA',
  'Accredited Green Building Council Professional on staff',
  'More than 30 years in the building industry',
  'Comprehensive range of ceiling, drywall, insulation and acoustic supplies',
  'Also distributes products from KNAUF, CAPCO, Casofour, Isover, Gyproc, RhinoROC, WoodUpp and Youngman',
  'Trusted by contractors and builders across the Western and Eastern Cape',
  'Convenient location in Joubert Industria, George',
  'Competitive pricing with flexible payment options',
];

function BrandLogos({ list }) {
  return (
    <div className="about-brands-grid">
      {list.map((brand) => (
        <Link to={`/partners#${brand.id}`} className="about-brand-card" key={brand.id} title={brand.name}>
          <img src={brand.logo} alt={`${brand.name} logo`} loading="lazy" />
        </Link>
      ))}
    </div>
  );
}

function About() {
  return (
    <div className="about-page">
      <SEO />

      <PageHero
        breadcrumbs={[{ name: 'About Us', path: '/about' }]}
        kicker={company.tagline}
        title="About OJANNA Ceiling & Drywall Distributors"
        text="Here to make a difference: George's ceiling and drywall suppliers to the building industry and the general public, distributing ceiling, drywall, insulation and acoustic products across the Garden Route and Western Cape. With more than 30 years in the industry and Green Building Council accreditation, we're your trusted partner for quality building materials."
        image={{
          src: '/images/shopfront.jpg',
          alt: `${company.name} showroom and delivery vehicles in Joubert Industria, George`,
          width: 1000,
          height: 652,
        }}
      />

      {/* Owner & mission */}
      <section className="section section-white">
        <div className="container grid grid-2">
          <div className="card about-owner">
            <span className="pill">Meet the owner</span>
            <h2>Niel Kasselman</h2>
            <p>
              Niel Kasselman is well-known in the building industry with more than three decades of experience. As an
              accredited Green Building Council Professional, Niel can assist with expert advice and recommend the
              correct products to build a more energy-efficient home or building.
            </p>
            <p>
              Niel and his team are ready to assist you with product selection, technical guidance, and project
              planning — whether you&apos;re a contractor, builder, or homeowner.
            </p>
            <Link to="/team" className="text-link">
              Meet the whole team <Icon name="arrow" size={16} />
            </Link>
          </div>
          <div className="card about-mission">
            <span className="pill">Our mission</span>
            <h2>The right solution, every time</h2>
            <p>{company.mission}</p>
            <p>
              <strong>Our vision:</strong> {company.vision}
            </p>
            <p>
              <strong>Our core values:</strong> {company.coreValues.join(', ')} — these guide every decision we make.
            </p>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="section section-light">
        <div className="container split">
          <img
            className="about-facility-img"
            src="/images/warehouse.jpg"
            alt="OJANNA Ceiling & Drywall Distributors warehouse in George stocked with ceiling boards, plasterboard and insulation"
            width="1200"
            height="808"
            loading="lazy"
          />
          <div className="prose">
            <span className="pill">Our facilities</span>
            <h2 className="section-title">Fully stocked &amp; ready to serve</h2>
            <p>
              Our warehouse in Joubert Industria, George is fully equipped to meet your project needs. With ample
              shelving, forklift operations, and a comprehensive range of ceiling, drywall, insulation and acoustic
              products, we keep materials organised and ready for quick dispatch.
            </p>
            <p>
              Easily accessible with parking and loading facilities, our premises make it simple to collect materials or
              arrange delivery across the Garden Route and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="section section-white">
        <div className="container">
          <div className="section-head">
            <span className="pill">Our partners</span>
            <h2 className="section-title">Trusted manufacturer partnerships</h2>
            <p className="section-subtitle">
              {company.name} is the master distributor for leading manufacturers, and also distributes products from
              other trusted brands
            </p>
          </div>
          <h3 className="about-brands-label">Master distributor for</h3>
          <BrandLogos list={masterBrands} />
          <h3 className="about-brands-label">Also distributing products from</h3>
          <BrandLogos list={additionalBrands} />
        </div>
      </section>

      {/* Values */}
      <section className="section section-light">
        <div className="container">
          <div className="section-head">
            <span className="pill">Our values</span>
            <h2 className="section-title">What sets us apart</h2>
            <p className="section-subtitle">Core values that drive everything we do at {company.name}</p>
          </div>
          <div className="grid grid-3">
            {VALUES.map((v) => (
              <div key={v.title} className="card">
                <div className="card-icon">
                  <Icon name={v.icon} />
                </div>
                <h3 className="card-title">{v.title}</h3>
                <p className="card-text">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="section section-white">
        <div className="container split split-top">
          <div>
            <span className="pill">Why choose us</span>
            <h2 className="section-title">Your trusted building partner</h2>
            <p className="section-subtitle mb-4">
              With more than 30 years in the building supplies industry, {company.name} has become the go-to
              choice for contractors, builders, and the general public throughout the Western and Eastern Cape.
            </p>
            <ul className="check-list">
              {WHY_CHOOSE.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="about-commitment">
            <h3>Our commitment</h3>
            <p>{company.mission}</p>
            <p>
              With more than three decades of expertise and {company.accreditation} status, we&apos;re here to deliver the right
              solution — superior service, competitive pricing, and a comprehensive range of materials to keep your
              projects on schedule.
            </p>
            <p>
              Guided by {company.coreValues.join(', ')} — these values shape every decision we make and every
              interaction we have.
            </p>
            <blockquote>
              “Build faster. Finish better. GET PAID SOONER”
              <cite>— Our promise to you</cite>
            </blockquote>
          </div>
        </div>
      </section>

      <CtaPanel text="Visit our showroom or contact us today for expert advice and competitive quotes." />
    </div>
  );
}

export default About;
